<template>
    <div class="assistant">
        <transition name="assistant-fade">
            <div v-if="visible" class="assistant-panel">
                <div class="assistant-header">
                    <div>
                        <div class="assistant-title">使用助手</div>
                        <div class="assistant-subtitle">基于使用手册回答</div>
                    </div>
                    <div class="assistant-header-actions">
                        <i class="el-icon-document-copy" title="复制整个对话" @click="copyConversation"></i>
                        <i class="el-icon-delete" title="清空对话" @click="clear"></i>
                        <i class="el-icon-close" title="关闭" @click="visible = false"></i>
                    </div>
                </div>

                <div ref="messageList" class="assistant-messages">
                    <div
                        v-for="(item, index) in messages"
                        :key="index"
                        :class="['assistant-message', item.role]"
                    >
                        <div class="assistant-bubble-wrap">
                            <div :class="['assistant-bubble', { busy: item.busy }]">{{ item.content }}</div>
                            <div
                                v-if="item.content"
                                class="assistant-copy"
                                title="复制内容"
                                @click="copy(item.content)"
                            >
                                <i class="el-icon-document-copy"></i>
                                <span>复制</span>
                            </div>
                        </div>
                    </div>
                    <div v-if="loading" class="assistant-message assistant">
                        <div class="assistant-bubble assistant-loading">正在查询手册，请稍等……</div>
                    </div>
                </div>

                <div v-if="showQuickQuestions" class="assistant-quick">
                    <span
                        v-for="item in quickQuestions"
                        :key="item"
                        class="assistant-quick-item"
                        @click="ask(item)"
                    >{{ item }}</span>
                </div>

                <div class="assistant-input">
                    <el-input
                        v-model="input"
                        type="textarea"
                        :rows="2"
                        resize="none"
                        maxlength="500"
                        placeholder="请输入问题，例如：怎么绑定设备？"
                        @keydown.native.enter.exact.prevent="send"
                    />
                    <el-button type="primary" size="small" :loading="loading" @click="send">发送</el-button>
                </div>
            </div>
        </transition>

        <div v-if="!visible" class="assistant-fab" title="使用助手" @click="open">
            <span class="assistant-fab-pulse"></span>
            <svg class="assistant-robot" viewBox="0 0 64 64" aria-hidden="true">
                <defs>
                    <linearGradient id="assistantRobotGradient" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stop-color="#ffffff" />
                        <stop offset="100%" stop-color="#dbe7ff" />
                    </linearGradient>
                </defs>
                <line x1="32" y1="11" x2="32" y2="18" stroke="#ffffff" stroke-width="3" stroke-linecap="round" />
                <circle cx="32" cy="9" r="3.2" fill="#7ef0c0" />
                <rect x="12" y="18" width="40" height="30" rx="12" fill="url(#assistantRobotGradient)" />
                <rect x="18" y="26" width="28" height="14" rx="7" fill="#173a9b" />
                <circle cx="25" cy="33" r="3.1" fill="#7ef0c0" />
                <circle cx="39" cy="33" r="3.1" fill="#7ef0c0" />
                <path d="M27 41 Q32 44 37 41" stroke="#173a9b" stroke-width="2.2" fill="none" stroke-linecap="round" />
                <rect x="17" y="49" width="30" height="5" rx="2.5" fill="#c7d8ff" />
            </svg>
        </div>
    </div>
</template>

<script>
export default {
    name: "Assistant",
    data() {
        return {
            visible: false,
            loading: false,
            input: "",
            messages: [],
            busyMessage: "当前使用人数较多，请稍后再试。",
            quickQuestions: [
                "怎么绑定设备？",
                "报警怎么处理？",
                "设备详情怎么看？",
                "怎么设置预警规则？",
                "怎么给设备缴费？"
            ]
        };
    },
    computed: {
        showQuickQuestions() {
            return this.messages.filter(item => !item.welcome).length === 0;
        }
    },
    methods: {
        open() {
            this.visible = true;
            if (this.messages.length === 0) {
                this.messages.push({
                    role: "assistant",
                    content: "你好，我是平台使用助手。你可以问我设备绑定、报警处理、故障处理、规则设置、缴费等操作问题。",
                    welcome: true
                });
            }
            this.scrollToBottom();
        },
        clear() {
            this.messages = [];
            this.open();
        },
        copyConversation() {
            const text = this.messages
                .filter(item => !item.welcome)
                .map(item => `${item.role === "user" ? "我" : "助手"}：${item.content}`)
                .join("\n\n");
            if (!text) {
                this.$message.info("暂无对话内容");
                return;
            }
            this.copy(text);
        },
        async copy(text) {
            try {
                if (navigator.clipboard && window.isSecureContext) {
                    await navigator.clipboard.writeText(text);
                } else {
                    const textarea = document.createElement("textarea");
                    textarea.value = text;
                    textarea.style.position = "fixed";
                    textarea.style.opacity = "0";
                    document.body.appendChild(textarea);
                    textarea.select();
                    document.execCommand("copy");
                    document.body.removeChild(textarea);
                }
                this.$message.success("已复制，可以继续提问");
            } catch (error) {
                this.$message.error("复制失败，请手动选择文字");
            }
        },
        ask(question) {
            this.input = question;
            this.send();
        },
        isBusyResponse(body) {
            if (!body) {
                return false;
            }
            if (body.code === 429 || body.error === "busy") {
                return true;
            }
            let text = "";
            try {
                text = JSON.stringify(body);
            } catch (error) {
                text = String(body);
            }
            return text.includes("1305") || text.includes("访问量过大") || text.includes("繁忙");
        },
        async send() {
            const question = (this.input || "").trim();
            if (!question || this.loading) {
                return;
            }

            const history = this.messages
                .filter(item => !item.welcome && !item.busy && (item.role === "user" || item.role === "assistant"))
                .slice(-6)
                .map(item => ({ role: item.role, content: item.content }));

            this.messages.push({ role: "user", content: question });
            this.input = "";
            this.loading = true;
            this.scrollToBottom();

            try {
                const res = await this.$API.assistant.chat.post({
                    question,
                    scene: "web",
                    history
                });
                const body = res && res.data ? res.data : res;
                if (body && body.code === 200 && body.data && body.data.answer) {
                    this.messages.push({ role: "assistant", content: body.data.answer });
                } else if (this.isBusyResponse(body)) {
                    this.input = question;
                    this.messages.push({ role: "assistant", content: this.busyMessage, busy: true });
                } else {
                    this.messages.push({
                        role: "assistant",
                        content: (body && body.message) ? body.message : "助手暂时不可用，请稍后再试。"
                    });
                }
            } catch (error) {
                const body = error && error.response && error.response.data ? error.response.data : null;
                if ((error && error.response && error.response.status === 429) || this.isBusyResponse(body)) {
                    this.input = question;
                    this.messages.push({ role: "assistant", content: this.busyMessage, busy: true });
                } else {
                    this.messages.push({
                        role: "assistant",
                        content: "助手暂时连接不上，请检查网络或稍后再试。"
                    });
                }
            } finally {
                this.loading = false;
                this.scrollToBottom();
            }
        },
        scrollToBottom() {
            this.$nextTick(() => {
                const el = this.$refs.messageList;
                if (el) {
                    el.scrollTop = el.scrollHeight;
                }
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.assistant-fab {
    position: fixed;
    right: 1.5vw;
    bottom: 8vh;
    z-index: 3000;
    width: 4vw;
    height: 4vw;
    max-width: 66px;
    max-height: 66px;
    border-radius: 50%;
    background: linear-gradient(135deg, #2f6bff 0%, #6a4dff 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    user-select: none;
    box-shadow: 0 10px 28px rgba(47, 107, 255, 0.38), 0 0 0 6px rgba(47, 107, 255, 0.08);
    animation: assistant-float 3.2s ease-in-out infinite;
    transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.assistant-fab:hover {
    transform: scale(1.08);
    box-shadow: 0 14px 34px rgba(47, 107, 255, 0.5), 0 0 0 8px rgba(47, 107, 255, 0.1);
    animation-play-state: paused;
}

.assistant-fab-pulse {
    position: absolute;
    inset: -8px;
    border-radius: 50%;
    border: 2px solid rgba(124, 168, 255, 0.38);
    animation: assistant-pulse 2.2s ease-out infinite;
}

.assistant-robot {
    width: 74%;
    height: 74%;
    position: relative;
    z-index: 1;
    filter: drop-shadow(0 2px 4px rgba(10, 30, 80, 0.18));
}

@keyframes assistant-float {
    0%,
    100% {
        transform: translateY(0);
    }
    50% {
        transform: translateY(-5px);
    }
}

@keyframes assistant-pulse {
    0% {
        transform: scale(0.92);
        opacity: 0.8;
    }
    70% {
        transform: scale(1.18);
        opacity: 0;
    }
    100% {
        opacity: 0;
    }
}

.assistant-panel {
    position: fixed;
    right: 1.5vw;
    bottom: 16vh;
    z-index: 3001;
    width: 24vw;
    max-width: 420px;
    height: 52vh;
    max-height: 560px;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 16px 48px rgba(15, 30, 60, 0.22);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.assistant-header {
    height: 64px;
    padding: 0 18px;
    background: linear-gradient(135deg, #2f6bff, #1f4fd8);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
}

.assistant-title {
    font-size: 16px;
    font-weight: 600;
    line-height: 22px;
}

.assistant-subtitle {
    font-size: 12px;
    opacity: 0.8;
    line-height: 18px;
}

.assistant-header-actions {
    display: flex;
    align-items: center;
    gap: 14px;

    i {
        cursor: pointer;
        font-size: 18px;
        opacity: 0.9;
    }

    i:hover {
        opacity: 1;
    }
}

.assistant-messages {
    flex: 1;
    padding: 16px;
    overflow-y: auto;
    background: #f5f7fb;
}

.assistant-message {
    display: flex;
    margin-bottom: 12px;

    &.user {
        justify-content: flex-end;
    }

    &.assistant {
        justify-content: flex-start;
    }
}

.assistant-bubble-wrap {
    max-width: 84%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.assistant-message.user .assistant-bubble-wrap {
    align-items: flex-end;
}

.assistant-bubble {
    max-width: 100%;
    padding: 10px 12px;
    border-radius: 12px;
    font-size: 13px;
    line-height: 1.7;
    white-space: pre-wrap;
    word-break: break-word;
}

.assistant-copy {
    margin-top: 4px;
    color: #909399;
    font-size: 12px;
    line-height: 18px;
    cursor: pointer;
    display: flex;
    align-items: center;

    i {
        margin-right: 4px;
        font-size: 13px;
    }
}

.assistant-copy:hover {
    color: #2f6bff;
}

.assistant-message.user .assistant-bubble {
    background: #2f6bff;
    color: #fff;
    border-bottom-right-radius: 4px;
}

.assistant-message.assistant .assistant-bubble {
    background: #fff;
    color: #303133;
    border: 1px solid #e8ebf2;
    border-bottom-left-radius: 4px;
}

.assistant-message.assistant .assistant-bubble.busy {
    background: #fff7e6;
    border-color: #ffd591;
    color: #ad6800;
}

.assistant-loading {
    color: #909399;
}

.assistant-quick {
    padding: 10px 14px 0;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    background: #fff;
    flex-shrink: 0;
}

.assistant-quick-item {
    padding: 5px 10px;
    border-radius: 14px;
    background: #f0f4ff;
    color: #2f6bff;
    font-size: 12px;
    cursor: pointer;
}

.assistant-quick-item:hover {
    background: #e0e9ff;
}

.assistant-input {
    padding: 12px 14px 14px;
    background: #fff;
    display: flex;
    align-items: flex-end;
    gap: 10px;
    flex-shrink: 0;

    .el-textarea {
        flex: 1;
    }

    .el-button {
        margin-bottom: 1px;
    }
}

.assistant-fade-enter-active,
.assistant-fade-leave-active {
    transition: opacity 0.18s ease, transform 0.18s ease;
}

.assistant-fade-enter,
.assistant-fade-leave-to {
    opacity: 0;
    transform: translateY(8px);
}

@media (max-width: 480px) {
    .assistant-panel {
        left: 10px;
        right: 10px;
        bottom: 76px;
        width: auto;
        height: 70vh;
    }

    .assistant-fab {
        right: 14px;
        bottom: 80px;
        width: 56px;
        height: 56px;
        max-width: 56px;
        max-height: 56px;
    }
}
</style>
